import React from 'react';
import { Icons } from '@/src/modules/components/Icons';
import { formatPrice } from '@/src/infrastructure/lib/currency';
import type { CreditPackView } from '../creditPacks.api';

export interface CreditPackCardProps {
  pack: CreditPackView;
  onSelect: (pack: CreditPackView) => void;
  isBusy?: boolean;
  /**
   * Highlights this card as the one the user picked. Deliberately NOT derived from
   * `pack.code`: an earlier version hardcoded
   * `isFeatured = code.includes('INTERMEDIATE')`, so the middle pack was always
   * ringed and badged "Most Popular" no matter what the user chose, and the
   * Advanced badge hardcoded the string "73 Credits" — which silently became a lie
   * the moment an admin repriced the pack (admin reprice is a supported operation,
   * see docs/architecture/commerce/02_PRODUCT_CATALOG.md §3a).
   */
  isSelected?: boolean;
  /** PQ-47: Error message displayed next to/under the buy button */
  errorMessage?: string | null;
}

export function CreditPackCard({
  pack,
  onSelect,
  isBusy = false,
  isSelected = false,
  errorMessage = null,
}: CreditPackCardProps) {
  const isWallet = pack.unit === 'VALUE';

  return (
    <div
      className={`relative flex flex-col rounded-3xl p-6 transition-all duration-200 ${
        isSelected
          ? 'bg-surface border-2 border-primary shadow-lg shadow-primary/5 ring-1 ring-primary/20'
          : 'bg-surface border border-outline-variant/40 hover:border-outline-variant/80 shadow-sm'
      }`}
      data-testid={`credit-pack-card-${pack.code}`}
      data-selected={isSelected ? 'true' : 'false'}
    >
      {/* Reflects the user's choice only — no pack is pre-promoted. */}
      {isSelected && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-primary text-on-primary text-xs font-bold tracking-wide uppercase shadow-sm">
          Selected
        </div>
      )}

      {isWallet ? (
        <>
          <div className="mb-4">
            <h3 className="text-xl font-bold text-on-surface">{pack.displayName}</h3>
          </div>
          <div className="my-2 pb-4 border-b border-outline-variant/30" data-testid="wallet-pack-pricing">
            <p className="text-sm text-on-surface-variant">Pay</p>
            <span className="text-4xl font-extrabold text-on-surface tracking-tight">
              {formatPrice(pack.amountPaise, pack.currency, { exact: true })}
            </span>
            <p className="text-sm text-on-surface-variant mt-3">Get wallet value</p>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-on-surface">₹{pack.credits.toLocaleString('en-IN')}</span>
              {pack.discountPercent > 0 && (
                <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-primary/10 text-primary" data-testid="wallet-pack-discount">
                  {pack.discountPercent}% off
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 py-2 px-3 my-3 rounded-xl bg-primary/5 text-primary text-xs font-medium">
            <Icons.Sparkles size={14} className="shrink-0" />
            <span>Wallet never expires · Spend on any template</span>
          </div>
          <div className="flex-1" />
        </>
      ) : (
        <>
      {/* Header */}
      <div className="mb-4">
        <h3 className="text-xl font-bold text-on-surface">{pack.displayName}</h3>
      </div>

      {/* Credits & Price */}
      <div className="my-2 pb-4 border-b border-outline-variant/30">
        <div className="flex items-baseline gap-2">
          <span className="text-4xl font-extrabold text-on-surface tracking-tight">
            {pack.credits}
          </span>
          <span className="text-base font-semibold text-on-surface-variant">Credits</span>
        </div>
        <div className="flex items-center gap-2 mt-2">
          <span className="text-2xl font-bold text-on-surface">
            {formatPrice(pack.amountPaise, pack.currency, { exact: true })}
          </span>
          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-surface-low text-on-surface-variant">
            ₹{pack.grossPricePerCredit.toFixed(2)} / credit
          </span>
        </div>
      </div>

      {/* Guarantee Badge */}
      <div className="flex items-center gap-2 py-2 px-3 my-3 rounded-xl bg-primary/5 text-primary text-xs font-medium">
        <Icons.Sparkles size={14} className="shrink-0" />
        <span>Credits never expire · Spend anytime</span>
      </div>

      <div className="flex-1" />
        </>
      )}

      {/* CTA Button */}
      <div className="mt-6 pt-4 border-t border-outline-variant/30">
        <button
          type="button"
          disabled={isBusy}
          onClick={() => onSelect(pack)}
          className={`w-full py-3 px-4 rounded-xl font-semibold text-sm transition-all flex items-center justify-center gap-2 ${
            isSelected
              ? 'bg-primary text-on-primary hover:bg-primary/90 shadow-sm active:scale-[0.99]'
              : 'bg-surface-high hover:bg-surface-highest text-on-surface border border-outline-variant/60 active:scale-[0.99]'
          } disabled:opacity-60 disabled:cursor-not-allowed`}
        >
          {isBusy ? (
            <>
              <Icons.Loader size={16} className="animate-spin" />
              <span>Processing...</span>
            </>
          ) : (
            <>
              <span>{isWallet ? `Add ${formatPrice(pack.amountPaise, pack.currency, { exact: true })} to wallet` : `Get ${pack.credits} Credits`}</span>
              <Icons.ArrowRight size={16} />
            </>
          )}
        </button>
        {errorMessage && (
          <p className="mt-2 text-xs font-semibold text-status-failed text-center" role="alert">
            {errorMessage}
          </p>
        )}
      </div>
    </div>
  );
}
